import React, { useContext, useEffect, useState } from "react";
import { Card, Row, Col, Spin } from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import { HomeOutlined, EyeOutlined, UserOutlined } from "@ant-design/icons";
import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { getSingleCustomer } from "../../../network/network";
import { AppContext } from "../../../context/AppContextProvider";
import CustomerTransactions from "./CustomerTransactions";
import { handleApiError } from "../../../utils/errorHandler";

const styles = {
  headerCard: {
    marginBottom: "20px",
  },
  pageHeader: {
    padding: "0",
  },
  detailsCard: (direction) => ({
    marginBottom: "20px",
    direction: direction === "rtl" ? "rtl" : "ltr",
  }),
  fieldLabel: {
    fontWeight: "bold",
    paddingBottom: "10px",
  },
  fieldValue: {
    border: "1px solid var(--color-border)",
    padding: "10px",
  },
};

const ViewCustomer = () => {
  const { appConfig } = useContext(AppContext);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const { t } = useTranslation();
  let { customerId } = useParams();

  useEffect(() => {
    setIsDataLoading(true);
    getSingleCustomer(customerId)
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setTableData(response.data.data.customer);
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [customerId, t]);

  const breadcrumbRoutes = [
    {
      path: "/",
      breadcrumbName: t("home"),
      icon: <HomeOutlined />,
    },
    {
      path: "customers",
      breadcrumbName: t("customers"),
      icon: <UserOutlined />,
    },
    {
      path: "",
      breadcrumbName: t("customer_details"),
      icon: <EyeOutlined />,
    },
  ];

  return (
    <>
      <Card style={styles.headerCard}>
        <CustomBreadcrumb menus={breadcrumbRoutes} />
        <PageHeader
          style={styles.pageHeader}
          title={t("customer_details")}
          onBack={() => window.history.back()}
          ghost={false}
          extra={false}
        />
      </Card>
      <Spin spinning={isDataLoading}>
        <Card style={styles.detailsCard(appConfig.direction)}>
          <Row gutter={[20, 20]}>
            <Col
              className="gutter-row"
              xs={24}
              sm={24}
              md={12}
              lg={12}
              xl={8}
              xxl={8}
            >
              <div style={styles.fieldLabel}>
                {t("name_ar")}
              </div>
              <div style={styles.fieldValue}>
                {tableData.name_ar ? tableData.name_ar : "-"}
              </div>
            </Col>
            <Col
              className="gutter-row"
              xs={24}
              sm={24}
              md={12}
              lg={12}
              xl={8}
              xxl={8}
            >
              <div style={styles.fieldLabel}>
                {t("name_en")}
              </div>
              <div style={styles.fieldValue}>
                {tableData.name_en ? tableData.name_en : "-"}
              </div>
            </Col>
            <Col
              className="gutter-row"
              xs={24}
              sm={24}
              md={12}
              lg={12}
              xl={8}
              xxl={8}
            >
              <div style={styles.fieldLabel}>
                {t("phone_number")}
              </div>
              <div style={styles.fieldValue}>
                {tableData.phone ? tableData.phone : "-"}
              </div>
            </Col>
            <Col
              className="gutter-row"
              xs={24}
              sm={24}
              md={12}
              lg={12}
              xl={8}
              xxl={8}
            >
              <div style={styles.fieldLabel}>
                {t("type")}
              </div>
              <div style={styles.fieldValue}>
                {tableData.user_type ? tableData.user_type : "-"}
              </div>
            </Col>
            {tableData.user_type === "company" && (
              <>
                <Col
                  className="gutter-row"
                  xs={24}
                  sm={24}
                  md={12}
                  lg={12}
                  xl={8}
                  xxl={8}
                >
                  <div style={styles.fieldLabel}>
                    {t("vat_number")}
                  </div>
                  <div style={styles.fieldValue}>
                    {tableData.vat ? tableData.vat : "-"}
                  </div>
                </Col>
                <Col
                  className="gutter-row"
                  xs={24}
                  sm={24}
                  md={12}
                  lg={12}
                  xl={8}
                  xxl={8}
                >
                  <div style={styles.fieldLabel}>
                    {t("cr_number")}
                  </div>
                  <div style={styles.fieldValue}>
                    {tableData.cr ? tableData.cr : "-"}
                  </div>
                </Col>
                <Col
                  className="gutter-row"
                  xs={24}
                  sm={24}
                  md={12}
                  lg={12}
                  xl={6}
                  xxl={6}
                >
                  <div style={styles.fieldLabel}>
                    {t("country")}
                  </div>
                  <div style={styles.fieldValue}>
                    {tableData.country ? tableData.country : "-"}
                  </div>
                </Col>
                <Col
                  className="gutter-row"
                  xs={24}
                  sm={24}
                  md={12}
                  lg={12}
                  xl={6}
                  xxl={6}
                >
                  <div style={styles.fieldLabel}>
                    {t("region")}
                  </div>
                  <div style={styles.fieldValue}>
                    {tableData.region
                      ? appConfig.direction === "rtl"
                        ? tableData.region?.name_ar
                        : tableData.region?.name_en
                      : "-"}
                  </div>
                </Col>
                <Col
                  className="gutter-row"
                  xs={24}
                  sm={24}
                  md={12}
                  lg={12}
                  xl={6}
                  xxl={6}
                >
                  <div style={styles.fieldLabel}>
                    {t("city")}
                  </div>
                  <div style={styles.fieldValue}>
                    {tableData.city
                      ? appConfig.direction === "rtl"
                        ? tableData.city?.name_ar
                        : tableData.city?.name_en
                      : "-"}
                  </div>
                </Col>
                <Col
                  className="gutter-row"
                  xs={24}
                  sm={24}
                  md={12}
                  lg={12}
                  xl={6}
                  xxl={6}
                >
                  <div style={styles.fieldLabel}>
                    {t("postal_code")}
                  </div>
                  <div style={styles.fieldValue}>
                    {tableData.postal_code ? tableData.postal_code : "-"}
                  </div>
                </Col>
                <Col
                  className="gutter-row"
                  xs={24}
                  sm={24}
                  md={12}
                  lg={12}
                  xl={8}
                  xxl={8}
                >
                  <div style={styles.fieldLabel}>
                    {t("street")}
                  </div>
                  <div style={styles.fieldValue}>
                    {tableData.street ? tableData.street : "-"}
                  </div>
                </Col>
                <Col
                  className="gutter-row"
                  xs={24}
                  sm={24}
                  md={12}
                  lg={12}
                  xl={8}
                  xxl={8}
                >
                  <div style={styles.fieldLabel}>
                    {t("building_number")}
                  </div>
                  <div style={styles.fieldValue}>
                    {tableData.building_number
                      ? tableData.building_number
                      : "-"}
                  </div>
                </Col>
                <Col
                  className="gutter-row"
                  xs={24}
                  sm={24}
                  md={12}
                  lg={12}
                  xl={8}
                  xxl={8}
                >
                  <div style={styles.fieldLabel}>
                    {t("plot_id_number")}
                  </div>
                  <div style={styles.fieldValue}>
                    {tableData.plot_id_number ? tableData.plot_id_number : "-"}
                  </div>
                </Col>
              </>
            )}
          </Row>
        </Card>
      </Spin>
      <CustomerTransactions />
    </>
  );
};

export default ViewCustomer;
